import { Link, useNavigate } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  Button,
  Input,
  Menu,
  MenuTrigger,
  MenuList,
  MenuItem,
  MenuPopover,
  Avatar,
  Divider,
  CounterBadge,
} from '@fluentui/react-components';
import {
  Search24Regular,
  Alert24Regular,
  SignOut24Regular,
  Person24Regular,
  WeatherMoon24Regular,
  WeatherSunny24Regular,
  Navigation24Regular,
  LocalLanguage24Regular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@app/providers/AuthProvider';
import { useTheme, useThemeStore, useActiveChromeColors } from '@app/providers/ThemeProvider';
import { useLanguage, supportedLanguages } from '@app/providers/I18nProvider';
import { useState, useEffect, useCallback } from 'react';
import { ReceptionSelector } from '@shared/components/selectors/ReceptionSelector';
import { AdminTenantSelector } from '@shared/components/navigation/AdminTenantSelector';
import { useEffectiveTenant } from '@shared/hooks/useEffectiveTenant';
import { useReceptionStore } from '@app/stores/receptionStore';
import { useGetApiV1NotificationsUserId } from '@api/generated/notifications/notifications/notifications';
import { NotificationPanel } from '@features/notifications/components/NotificationPanel';
// Orval-generated type
import type { ReceptionDto } from '@api/generated/visitor-api/model';

const useStyles = makeStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    gap: tokens.spacingHorizontalM,
  },
  headerAdmin: {
    backgroundColor: '#a40000',
    color: 'white',
  },
  left: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    textDecoration: 'none',
    color: 'inherit',
  },
  logoImage: {
    height: '32px',
    objectFit: 'contain',
  },
  logoText: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
  },
  center: {
    flex: 1,
    maxWidth: '400px',
    '@media (max-width: 768px)': {
      display: 'none',
    },
  },
  right: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  mobileMenuButton: {
    display: 'none',
    '@media (max-width: 768px)': {
      display: 'flex',
    },
  },
  iconButton: {
    minWidth: '32px',
  },
  receptionSelector: {
    marginLeft: tokens.spacingHorizontalM,
  },
  tenantSelector: {
    marginLeft: tokens.spacingHorizontalM,
  },
});

interface HeaderProps {
  isAdmin?: boolean;
  onToggleMobileMenu?: () => void;
}

export function Header({ isAdmin = false, onToggleMobileMenu }: HeaderProps) {
  const styles = useStyles();
  const { t } = useTranslation('navigation');
  const navigate = useNavigate();
  const { user, logout, hasAnyRole } = useAuth();
  const { effectiveMode, toggleMode } = useTheme();
  const { logoUrl } = useThemeStore();
  const { headerColor } = useActiveChromeColors();
  const { currentLanguage, changeLanguage } = useLanguage();
  const [searchValue, setSearchValue] = useState('');
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);

  // Reception selector state (for gate_keeper and admin roles)
  const { selectedReceptionId, loadSelectedReception, saveSelectedReception } = useReceptionStore();
  const showReceptionSelector = hasAnyRole(['gate_keeper', 'admin']);

  // Tenant switcher in the admin header is platform_admin-only (Phase 51 D-04)
  const { isPlatformAdmin } = useEffectiveTenant();

  // Global employee search is admin-only (mirrors backend [Authorize(Roles = "admin")] on /api/v1/employee-management/employee/search)
  const showGlobalSearch = hasAnyRole(['admin']);

  // Get user's ssoId for profile operations
  const ssoId = user?.userId || '';

  // Load selected reception on mount
  useEffect(() => {
    if (ssoId && showReceptionSelector) {
      loadSelectedReception(ssoId);
    }
  }, [ssoId, showReceptionSelector, loadSelectedReception]);

  // Handle reception change
  const handleReceptionChange = useCallback(
    async (receptionId: string, _reception?: ReceptionDto) => {
      if (ssoId && receptionId && receptionId !== '00000000-0000-0000-0000-000000000000') {
        await saveSelectedReception(ssoId, receptionId);
        // Reload page to apply new reception context (like Blazor does)
        window.location.reload();
      }
    },
    [ssoId, saveSelectedReception]
  );

  // Load notification count (matching Blazor MainLayout.LoadNotifications)
  // Backend requires userId to fetch notifications
  // All notifications are "unread" until deleted, so count = total
  const { data: notifications } = useGetApiV1NotificationsUserId<{ id: string }[]>(ssoId, {
    query: {
      enabled: !!user && !!ssoId,
      refetchInterval: 60000, // Refresh every minute (like Blazor)
      staleTime: 30000, // Consider data stale after 30 seconds
    },
  });
  const notificationCount = notifications?.length ?? 0;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchValue.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchValue)}`);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const displayName = user?.name || user?.preferredUsername || 'User';
  const initials = displayName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  // Determine if header color is dark (needs white text)
  const isDarkHeader = headerColor && !isAdmin &&
    (['#0078d4', '#002050', '#5c2d91', '#107c10', '#008272', '#e81123'].includes(headerColor.toLowerCase()) ||
     headerColor.toLowerCase().startsWith('#0') || headerColor.toLowerCase().startsWith('#1') ||
     headerColor.toLowerCase().startsWith('#2') || headerColor.toLowerCase().startsWith('#3'));

  return (
    <header
      className={`${styles.header} ${isAdmin ? styles.headerAdmin : ''}`}
      style={!isAdmin ? {
        backgroundColor: headerColor,
        color: isDarkHeader ? 'white' : undefined,
      } : undefined}
    >
      <div className={styles.left}>
        <Button
          className={styles.mobileMenuButton}
          appearance="subtle"
          icon={<Navigation24Regular />}
          aria-label="Toggle menu"
          onClick={onToggleMobileMenu}
        />
        <Link to={isAdmin ? '/admin' : '/'} className={styles.logo}>
          {logoUrl ? (
            <img src={logoUrl} alt="Logo" className={styles.logoImage} />
          ) : (
            <span className={styles.logoText}>Maja{isAdmin ? ' Admin' : ''}</span>
          )}
        </Link>

        {/* Reception Selector - only for gate_keeper and admin roles */}
        {showReceptionSelector && (
          <div className={styles.receptionSelector}>
            <ReceptionSelector
              selectedReceptionId={selectedReceptionId || undefined}
              onReceptionChange={handleReceptionChange}
              showIcon={true}
            />
          </div>
        )}

        {/* Tenant Selector - platform_admin only, admin area only (Phase 51 D-04) */}
        {isAdmin && isPlatformAdmin && (
          <div className={styles.tenantSelector}>
            <AdminTenantSelector />
          </div>
        )}
      </div>

      <div className={styles.center}>
        {showGlobalSearch && (
          <form onSubmit={handleSearch}>
            <Input
              placeholder={t('search')}
              contentBefore={<Search24Regular />}
              value={searchValue}
              onChange={(_, data) => setSearchValue(data.value)}
              style={{ width: '100%' }}
            />
          </form>
        )}
      </div>

      <div className={styles.right}>
        {/* Theme Toggle */}
        <Button
          appearance="subtle"
          icon={effectiveMode === 'dark' ? <WeatherSunny24Regular /> : <WeatherMoon24Regular />}
          onClick={toggleMode}
          aria-label={effectiveMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          className={styles.iconButton}
        />

        {/* Language Selector */}
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <Button
              appearance="subtle"
              icon={<LocalLanguage24Regular />}
              className={styles.iconButton}
              aria-label="Change language"
            />
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              {supportedLanguages.map((lang) => (
                <MenuItem
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  icon={<span>{lang.flag}</span>}
                >
                  {lang.name}
                  {currentLanguage.code === lang.code && ' ✓'}
                </MenuItem>
              ))}
            </MenuList>
          </MenuPopover>
        </Menu>

        {/* Notifications - matching Blazor's bell icon with counter badge */}
        <div style={{ position: 'relative', display: 'inline-flex' }}>
          <Button
            appearance="subtle"
            icon={<Alert24Regular />}
            onClick={() => setNotificationPanelOpen(true)}
            aria-label="Notifications"
            className={styles.iconButton}
          />
          {notificationCount > 0 && (
            <CounterBadge
              count={notificationCount}
              color="danger"
              size="small"
              style={{ position: 'absolute', top: 0, right: 0 }}
            />
          )}
        </div>
        <NotificationPanel
          open={notificationPanelOpen}
          onClose={() => setNotificationPanelOpen(false)}
        />

        {/* User Menu */}
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <Button appearance="subtle" aria-label="User menu">
              <Avatar name={displayName} initials={initials} size={28} />
            </Button>
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItem icon={<Person24Regular />} onClick={() => navigate('/profile')}>
                {t('profile')}
              </MenuItem>
              <Divider />
              <MenuItem icon={<SignOut24Regular />} onClick={handleLogout}>
                {t('logout')}
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
      </div>
    </header>
  );
}
